// Mockup Board API contract — keep byte-identical with frontend/src/modules/mockup-board/types.ts
export type MockupStatus = 'pending' | 'approved' | 'rejected' | 'superseded';
export type BuildStatus = 'not_started' | 'building' | 'live';
export type RenderStatus = 'queued' | 'ready' | 'failed';
export type BoardTab = 'pending' | 'rejected' | 'approved';

export interface PersonRef { id: string; name: string }
/** A question Ardhanex asks on one mockup version (2026-10-12; spec docs/_mockups/mockup-board-questions/SPEC.md). */
export interface MockupQuestion {
  id: string;
  position: number;                // 1-based, order shown
  text: string;
  answer: string | null;
  answeredBy: PersonRef | null;
  answeredAt: string | null;       // ISO
}
export interface MockupVersionRef { id: string; version: number; status: MockupStatus }

export interface MockupItem {
  id: string;
  seriesId: string;
  version: number;
  title: string;
  feature: string;
  note: string | null;             // uploader's note for the reviewer
  status: MockupStatus;
  createdAt: string;               // ISO
  uploadedVia: 'ui' | 'token';
  uploadedBy: PersonRef | null;    // null when a token uploaded it
  decidedBy: PersonRef | null;
  decidedAt: string | null;        // ISO
  decisionNotes: string | null;    // approve notes (optional) or reject notes (required)
  buildStatus: BuildStatus | null; // null until approved
  buildStatusAt: string | null;
  renderStatus: RenderStatus;
  renderTruncated: boolean;        // JPEG cut at 16000 px
  externalRefs: number;            // outside links blocked in the frame
  htmlBytes: number;
  replaces: MockupVersionRef | null;
  newerVersion: MockupVersionRef | null;
  questions: MockupQuestion[];     // [] when none; ordered by position
  pinCounts: { draft: number; sent: number }; // notes on the mockup picture (2026-10-12 pins)
}

/** A note on a spot of the mockup's full-page JPEG (2026-10-12; spec docs/_mockups/mockup-board-pins/SPEC.md). */
export type MockupPinStatus = 'draft' | 'sent' | 'dropped';
export interface MockupPin {
  id: string;
  n: number;                       // number on the dot, per version, never reused
  x: number;                       // % of the JPEG width, 0–100, 2 decimals
  y: number;                       // % of the JPEG height, 0–100, 2 decimals
  text: string;
  author: PersonRef;
  status: MockupPinStatus;
  decision: 'approve' | 'reject' | null;
  createdAt: string;               // ISO
  updatedAt: string;               // ISO
}
export interface PinInput { x: number; y: number; text: string }
export interface PinEditBody { text: string }
export interface MockupPinListResponse { items: MockupPin[] } // ordered by n; never 'dropped'

export interface MockupListResponse {
  items: MockupItem[];
  counts: { pending: number; rejected: number; approved: number };
  /** SUN-105: page notes of non-admin writers for this tab (owner/admin only; [] for others). Their number is inside `counts`. */
  pageNotes?: ApprovalNote[];
}
export interface ViewUrlResponse { url: string; expiresIn: number }
export interface AnswerInput { questionId: string; answer: string }
export interface DecisionBody { notes?: string | null; answers?: AnswerInput[]; pinIds?: string[] }
export interface AddQuestionsBody { questions: string[] }
/** GET /mockups/:id/answers — the Mac pull (mbk_ token or super_admin). */
export interface MockupAnswersExport {
  id: string;
  title: string;
  version: number;
  status: MockupStatus;
  decidedBy: string | null;
  decidedAt: string | null;
  notes: string | null;
  questions: { position: number; text: string; answer: string | null; answeredBy: string | null; answeredAt: string | null }[];
  pins: { n: number; x: number; y: number; text: string; author: string; decision: 'approve' | 'reject' | null; cropUrl: string }[]; // sent only
}
export interface BuildStatusBody { buildStatus: BuildStatus }

// Every JSON response: { success: true, data: T } or { success: false, error: string }
export type ApiResult<T> = { success: true; data: T } | { success: false; error: string };

/** SUN-50: an upload that still links to a local img/ folder (browser AND server refuse it). */
export const IMG_FOLDER_RE = /(src\s*=\s*|url\(\s*)["']?img\//i;

export const MOCKUP_ERRORS = {
  notOn: 'Mockup Board is not switched on',
  viewRole: 'Only the owner, admins and Ardhanex can open the Mockup Board',
  decideRole: "Only the customer's owner or admin can approve or reject.",
  ardhanexOnly: 'Only Ardhanex can do this',
  notFound: 'Mockup not found',
  notPending: 'This mockup is no longer waiting for a decision',
  notesRequired: 'Write your notes before you reject.',
  notesLength: 'Notes must be 3 to 2000 characters',
  tooBig: 'The file is larger than 15 MB',
  notHtml: 'The file must be one HTML page (.html)',
  imgFolder: 'This file links to an img/ folder. Use the upload script, which packs the images in.',
  duplicate: 'This exact file is already in this mockup',
  staleVersion: 'A newer version of this mockup already exists',
  approvedNoVersion: 'This mockup is approved. Upload a new mockup instead',
  notApproved: 'Build status can only be set on an approved mockup',
  badTicket: 'This view link has expired. Open the mockup again',
  rate: 'Too many requests. Try again in an hour',
  answersRequired: 'Answer every question before you approve.',
  answerLength: 'Each answer must be 2 to 2000 characters',
  questionsLimit: 'Questions: up to 10, each 1 to 300 characters',
  questionsLocked: 'Questions can only be added while the mockup waits for a decision',
  noNewQuestions: 'No new questions to add',
  questionsChanged: 'Ardhanex just added a question. Close this and open Approve again.',
  unknownQuestion: 'This question is not on this mockup',
  notesOrPins: 'Write your notes or add a note on the mockup before you reject.',
  pinText: 'Write a note of 1 to 1000 characters',
  pinSpot: 'The spot is not on the mockup',
  pinLimit: 'Up to 30 notes on one mockup',
  pinClosed: 'This mockup is decided. Notes are closed.',
  pinNotYours: 'Only the person who wrote this note can change it',
  noPicture: 'The picture is not ready. Comment is off for now.',
  badPins: 'A note you sent is not on this mockup. Open the mockup again.',
} as const;
export const QUESTION_LIMITS = { perMockup: 10, question: 300, answerMin: 2, answer: 2000 } as const;
export const PIN_LIMITS = { perMockup: 30, text: 1000, perUserHour: 60, cropW: 480, cropH: 270 } as const;

// ── Page notes (2026-10-10 add-on; spec docs/_mockups/page-notes/SPEC.md) ──
/** 2026-10-12 Send: 'draft' (private to its writer) → Send → 'open' (shown as "Sent") → 'seen' → 'fixed'. */
export type PageNoteStatus = 'draft' | 'pending_approval' | 'approved' | 'rejected' | 'open' | 'seen' | 'fixed';
export type PageNoteFilter = 'all' | 'open' | 'seen' | 'fixed';   // owner/admin list (never drafts)
export type MyNotesTab = 'drafts' | 'sent' | 'seen' | 'fixed';
export interface PageNoteBox { x: number; y: number; width: number; height: number }
export interface PageNoteViewport { width: number; height: number; dpr: number; ua: string }

/** Body of POST /page-notes — built in the browser from the agentation annotation. */
export interface PageNoteInput {
  clientId: string;                // annotation.id from the package
  route: string;                   // location.pathname + scrubbed search, starts with one '/'
  comment: string;
  element: string;
  elementPath: string;
  fullPath?: string | null;
  selectedText?: string | null;
  nearbyText?: string | null;
  cssClasses?: string | null;
  box?: PageNoteBox | null;
  viewport: PageNoteViewport;
  /** SUN-88: the note was written on the Test Phone screen (admin/owner only; the server then reads the phone). */
  onPhone?: boolean;
}

export interface PageNote {
  id: string;
  clientId: string;
  route: string;
  comment: string;
  element: string;
  elementPath: string;
  fullPath: string | null;
  selectedText: string | null;
  nearbyText: string | null;
  cssClasses: string | null;
  box: PageNoteBox | null;
  viewport: PageNoteViewport | null;
  author: PersonRef;
  authorRole: string;
  status: PageNoteStatus;
  reply: string | null;
  statusAt: string | null;         // ISO
  sentAt: string | null;           // ISO; null while draft
  batch: { id: string; number: number } | null; // 2026-10-12 batches: null while draft
  createdAt: string;               // ISO
  updatedAt: string;               // ISO
  /** SUN-81, My notes only: SS Lead's open question, the writer's answer, and "Fixed? Yes / Still broken". */
  kiaan?: PageNoteKiaan | null;
  /** SUN-88: written on the Test Phone screen — what was on the phone. Absent for every other note. */
  phone?: PageNotePhone | null;
}
export interface PageNotePhone { appKey: string | null; appName: string | null; appVersion: string | null; package: string | null; activity: string | null; hasShot: boolean; label: string }
export interface PageNoteKiaan { question: string | null; answer: string | null; verdict: string | null; askVerdict: boolean }

export interface PageNoteListResponse {
  items: PageNote[];
  counts: { all: number; open: number; seen: number; fixed: number };
  roleCounts: Record<string, number>;   // authorRole → sent notes (ignores the role filter)
}
/** GET /page-notes/mine?tab=drafts&q= — the caller's own draft notes. counts: drafts = notes, the rest = batches. */
export interface MyNotesResponse { tab: 'drafts'; items: PageNote[]; counts: MyNotesCounts }
/** SUN-105: counts also carry `waiting` / `rejected` (notes) once the approval table is applied. */
export type MyNotesCounts = Record<MyNotesTab, number> & { waiting?: number; rejected?: number };
/** SUN-105 GET /page-notes/mine?tab=waiting|rejected — the writer's notes held for approval / turned down (notes, newest first). */
export interface MyApprovalNote extends PageNote { rejectReason: string | null; rejectedBy: string | null; rejectedAt: string | null }
export interface MyApprovalResponse { tab: 'waiting' | 'rejected'; items: MyApprovalNote[]; counts: MyNotesCounts }
/** A batch in My notes: `notes` = only this tab's notes (and only matches when searching). */
export type MyBatch = PageNoteBatch & { notes: PageNote[] };
/** GET /page-notes/mine?tab=sent|seen|fixed&q=&cursor=&limit=5 — own batches with ≥1 note of that status, newest first. */
export interface MyBatchesResponse { tab: 'sent' | 'seen' | 'fixed'; batches: MyBatch[]; nextCursor: string | null; counts: MyNotesCounts }
/** POST /page-notes/send — sent:0 + batchId/batchNumber null when there was nothing to send. */
export interface PageNoteSendResponse { sent: number; batchId: string | null; batchNumber: number | null; /** SUN-105: true = the notes wait for an owner/admin (non-admin writer) */ pendingApproval?: boolean }
/** SUN-105: one page note in the owner/admin approval views (Mockup Board tabs, queue, Open page bar). */
export interface ApprovalNote {
  id: string; clientId: string; route: string; element: string | null; comment: string;
  authorId: string; authorName: string; authorRole: string;
  batchId: string; batchNumber: number; sentAt: string; createdAt: string;
  status: 'pending_approval' | 'approved' | 'rejected' | 'open' | 'seen' | 'fixed';
  decision: 'pending' | 'approved' | 'rejected';
  reason: string | null; decidedByName: string | null; decidedAt: string | null;
  released: boolean;
  moreFromAuthor: number; // other pending notes from the same writer
}
/** 2026-10-12 one Send = one batch, numbered per tenant. Status is derived from its notes. */
export type PageNoteBatchStatus = 'sent' | 'seen' | 'fixed';
export interface PageNoteBatch {
  id: string;
  number: number;
  author: PersonRef;
  authorRole: string;              // role at send time
  noteCount: number;
  sentAt: string;                  // ISO
  status: PageNoteBatchStatus;
  summary: { sent: number; seen: number; fixed: number };
}
/** GET/PUT /page-notes/settings — who can write page notes (owner/admin always, locked). */
export interface PageNoteSettings { roles: string[]; choices: { name: string; label: string; locked: boolean }[] }
export interface PageNoteEditBody { comment: string }
export interface PageNoteStatusBody { status: PageNoteStatus; reply?: string | null }
export interface PageNoteExportResponse { exportedAt: string; items: PageNote[] }

export const PAGE_NOTE_LIMITS = {
  comment: 2000, element: 200, elementPath: 500, fullPath: 1000, selectedText: 500, nearbyText: 500,
  cssClasses: 300, route: 500, clientId: 40, reply: 2000, ua: 60, bodyBytes: 16384,
  perUserHour: 60, perTenantDay: 500, statusPerHour: 120, sendPerMinute: 6,
} as const;
export const PAGE_NOTE_DROP_QUERY_KEYS = ['token', 'vt', 'code', 'otp', 'password'] as const;

export const PAGE_NOTE_ERRORS = {
  notOn: 'Page notes are not switched on',
  writeRole: 'Your role cannot write page notes',
  notFound: 'Page note not found',
  notOpen: 'This note is no longer open. It cannot be changed',
  commentLength: 'Write a note of 1 to 2000 characters',
  tooLong: 'A note field is too long',
  incomplete: 'This note is not complete',
  badRoute: 'The page address is not valid',
  tooBig: 'This note is too big',
  rate: 'Too many page notes. Try again later.',
  badStatus: 'Status must be open, seen or fixed',
  notDraft: 'This note was sent. It cannot be changed',
  badRoles: 'Pick roles from the list',
  replyLength: 'Reply must be up to 2000 characters',
} as const;
